import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppSidebar from '@/components/AppSidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Recycle, Package, Truck, MapPin, Boxes, AlertTriangle, CheckCircle2,
  FileCheck, Plus, RefreshCw, Battery, ChevronRight, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_META = {
  generated: { label: 'Generated', icon: Package, color: 'text-gray-600', bg: 'bg-gray-100', bar: 'bg-gray-500' },
  picked_up: { label: 'Picked Up', icon: Truck, color: 'text-blue-600', bg: 'bg-blue-100', bar: 'bg-blue-500' },
  in_transit: { label: 'In Transit', icon: MapPin, color: 'text-amber-600', bg: 'bg-amber-100', bar: 'bg-amber-500' },
  in_storage: { label: 'In Storage', icon: Boxes, color: 'text-orange-600', bg: 'bg-orange-100', bar: 'bg-orange-500' },
  at_facility: { label: 'At Facility', icon: AlertTriangle, color: 'text-purple-600', bg: 'bg-purple-100', bar: 'bg-purple-500' },
  disposed: { label: 'Disposed', icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-100', bar: 'bg-green-500' },
  certified: { label: 'Certified', icon: FileCheck, color: 'text-emerald-600', bg: 'bg-emerald-100', bar: 'bg-emerald-500' },
};

const CONTAINER_TYPES = ['UN-approved steel drum', 'IBC tote', 'Battery transport crate', 'Sealed Li-ion container', 'Pallet box'];
const CHEMS = ['LFP', 'NMC', 'LCO', 'Sodium-ion'];

const StatusBadge = ({ status }) => {
  const m = STAGE_META[status] || STAGE_META.generated;
  const Icon = m.icon;
  return (
    <span data-testid={`load-status-${status}`} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${m.bg} ${m.color}`}>
      <Icon className="w-3.5 h-3.5" />{m.label}
    </span>
  );
};

export default function RecyclingPage() {
  const navigate = useNavigate();
  const [user] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const isGuest = user?.role === 'guest';
  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [loads, setLoads] = useState([]);
  const [eolAssets, setEolAssets] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [stageFilter, setStageFilter] = useState(null);
  const [form, setForm] = useState({
    generator_name: '', generator_address: '', chemistry: 'LFP',
    quantity: '', quantity_unit: 'kg', container_type: CONTAINER_TYPES[0], description: '',
  });

  const loadAll = useCallback(async () => {
    try {
      const [s, l, e] = await Promise.all([
        axios.get(`${API}/recycling/summary`, config),
        axios.get(`${API}/recycling/loads`, config),
        axios.get(`${API}/recycling/eol-assets`, config),
      ]);
      setSummary(s.data); setLoads(l.data); setEolAssets(e.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.clear(); navigate('/login'); }
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const createManual = async () => {
    if (!form.generator_name || !form.generator_address || !form.quantity) {
      toast.error('Generator, address and quantity are required'); return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/recycling/loads`, { ...form, quantity: parseFloat(form.quantity) }, config);
      toast.success(`Waste load ${res.data.manifest_no} created`);
      setDialogOpen(false);
      setForm({ generator_name: '', generator_address: '', chemistry: 'LFP', quantity: '', quantity_unit: 'kg', container_type: CONTAINER_TYPES[0], description: '' });
      await loadAll();
      navigate(`/recycling/${res.data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
    finally { setCreating(false); }
  };

  const createFromAsset = async (assetId) => {
    try {
      const res = await axios.post(`${API}/recycling/loads/from-asset/${assetId}`, {}, config);
      toast.success(`Manifest ${res.data.manifest_no} created from asset`);
      setDialogOpen(false);
      await loadAll();
      navigate(`/recycling/${res.data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const regen = async () => {
    try { await axios.post(`${API}/recycling/generate`, {}, config); toast.success('Demo data regenerated'); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const eolBadge = (s) => s === 'Eligible' ? 'bg-red-100 text-red-700' : s === 'Approaching' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700';

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <AppSidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-black flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Recycle className="w-6 h-6 text-emerald-600" /> Recycling &amp; Disposal
              </h1>
              <p className="text-sm text-gray-500">End-of-life BESS waste-load tracking · chain of custody · movement verification</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={loadAll} data-testid="recycling-refresh"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
              {!isGuest && (
                <>
                  <Button variant="outline" size="sm" onClick={regen} data-testid="recycling-generate">Generate Test Data</Button>
                  <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-waste-load-btn">
                        <Plus className="w-4 h-4 mr-1" />New Waste Load
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                      <DialogHeader><DialogTitle>Create Waste Load</DialogTitle></DialogHeader>
                      <Tabs defaultValue="manual">
                        <TabsList className="grid grid-cols-2 w-full">
                          <TabsTrigger value="manual" data-testid="tab-manual">Manual Entry</TabsTrigger>
                          <TabsTrigger value="asset" data-testid="tab-from-asset">From EOL Asset</TabsTrigger>
                        </TabsList>
                        <TabsContent value="manual" className="space-y-3 pt-3">
                          <div><Label>Generator Name *</Label><Input data-testid="form-generator" value={form.generator_name} onChange={(e) => setForm({ ...form, generator_name: e.target.value })} placeholder="e.g. BESS-Carnegie" /></div>
                          <div><Label>Generator Address *</Label><Input data-testid="form-address" value={form.generator_address} onChange={(e) => setForm({ ...form, generator_address: e.target.value })} placeholder="Suburb, State" /></div>
                          <div className="grid grid-cols-2 gap-3">
                            <div><Label>Chemistry</Label>
                              <Select value={form.chemistry} onValueChange={(v) => setForm({ ...form, chemistry: v })}>
                                <SelectTrigger data-testid="form-chemistry"><SelectValue /></SelectTrigger>
                                <SelectContent>{CHEMS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div><Label>Quantity (kg) *</Label><Input data-testid="form-quantity" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="0" /></div>
                          </div>
                          <div><Label>Container Type</Label>
                            <Select value={form.container_type} onValueChange={(v) => setForm({ ...form, container_type: v })}>
                              <SelectTrigger data-testid="form-container"><SelectValue /></SelectTrigger>
                              <SelectContent>{CONTAINER_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div><Label>Description</Label><Textarea data-testid="form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Notes about the waste load" rows={2} /></div>
                          <DialogFooter>
                            <Button onClick={createManual} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="create-load-submit">
                              {creating ? 'Creating…' : 'Create Manifest'}
                            </Button>
                          </DialogFooter>
                        </TabsContent>
                        <TabsContent value="asset" className="pt-3">
                          <p className="text-sm text-gray-500 mb-2">Assets ranked by end-of-life readiness (lowest SOH first).</p>
                          <div className="space-y-2 max-h-80 overflow-y-auto">
                            {eolAssets.map((a) => (
                              <div key={a.id} className="flex items-center justify-between border border-gray-100 rounded-lg p-2.5">
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-black flex items-center gap-1.5"><Battery className="w-3.5 h-3.5 text-gray-400" />{a.name}</div>
                                  <div className="text-xs text-gray-500">{a.chemistry} · SOH {a.soh}% · {a.location}</div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${eolBadge(a.eol_status)}`}>{a.eol_status}</span>
                                  <Button size="sm" variant="outline" disabled={a.has_manifest} onClick={() => createFromAsset(a.id)} data-testid={`from-asset-${a.id}`}>
                                    {a.has_manifest ? 'Has manifest' : 'Create'}
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </TabsContent>
                      </Tabs>
                    </DialogContent>
                  </Dialog>
                </>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          {loading ? <div className="flex items-center justify-center h-64 text-gray-400">Loading waste loads…</div> : (
            <>
              {/* Total active */}
              <Card className={`mb-5 border-gray-200 cursor-pointer transition-shadow hover:shadow-md ${stageFilter === null ? 'ring-2 ring-emerald-500' : ''}`} onClick={() => setStageFilter(null)} data-testid="total-active-card">
                <CardContent className="py-6 text-center">
                  <div className="text-5xl font-bold text-black" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="total-active">{summary?.total_active ?? 0}</div>
                  <div className="text-sm text-gray-500 mt-1">Total Active Waste Loads{stageFilter ? ' · click to show all' : ''}</div>
                </CardContent>
              </Card>

              {/* Stage cards (click to filter) */}
              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-6">
                {(summary?.stages || []).map((st) => {
                  const m = STAGE_META[st.key];
                  const Icon = m.icon;
                  const pct = summary.total_active ? (st.count / summary.total_active) * 100 : 0;
                  const active = stageFilter === st.key;
                  return (
                    <button
                      key={st.key}
                      onClick={() => setStageFilter(active ? null : st.key)}
                      className={`text-left bg-white border rounded-xl p-3 transition-all hover:shadow-md ${active ? 'border-emerald-500 ring-2 ring-emerald-500' : 'border-gray-200'}`}
                      data-testid={`stage-card-${st.key}`}
                      aria-pressed={active}
                    >
                      <div className={`w-9 h-9 rounded-full ${m.bg} flex items-center justify-center mb-2`}><Icon className={`w-5 h-5 ${m.color}`} /></div>
                      <div className="text-2xl font-bold text-black">{st.count}</div>
                      <div className="text-xs text-gray-500 mb-2">{m.label}</div>
                      <div className="h-1 rounded-full bg-gray-100 overflow-hidden"><div className={`h-full ${m.bar}`} style={{ width: `${pct}%` }} /></div>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h2 className="text-lg font-bold text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {stageFilter ? `${STAGE_META[stageFilter].label} Waste Loads` : 'Waste Load Tracking — Complete Status Overview'}
                </h2>
                {stageFilter && (
                  <Button variant="outline" size="sm" onClick={() => setStageFilter(null)} data-testid="clear-stage-filter">
                    <X className="w-4 h-4 mr-1" />Show all loads
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                {(() => {
                  const filtered = stageFilter ? loads.filter((l) => l.status === stageFilter) : loads;
                  if (filtered.length === 0) {
                    return <p className="text-gray-400 text-sm" data-testid="no-loads-msg">{stageFilter ? `No waste loads in "${STAGE_META[stageFilter].label}".` : 'No waste loads yet.'}</p>;
                  }
                  return filtered.map((l) => (
                  <Card key={l.id} className="border-gray-200 hover:shadow-md transition-shadow cursor-pointer" data-testid={`load-card-${l.id}`} onClick={() => navigate(`/recycling/${l.id}`)}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-black">Manifest #{l.manifest_no}</span>
                          <StatusBadge status={l.status} />
                        </div>
                        <button className="text-sm text-emerald-600 font-medium flex items-center gap-1" data-testid={`view-details-${l.id}`}>View Details <ChevronRight className="w-4 h-4" /></button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3 text-sm">
                        <div><div className="text-gray-400 text-xs">Generator</div><div className="text-gray-800">{l.generator_name}</div></div>
                        <div><div className="text-gray-400 text-xs">Waste Type</div><div className="text-gray-800">{l.waste_type}</div></div>
                        <div><div className="text-gray-400 text-xs">Quantity</div><div className="text-gray-800">{l.quantity} {l.quantity_unit}</div></div>
                      </div>
                      <div className="text-xs text-gray-400 mt-2 flex items-center gap-3 flex-wrap">
                        <span>Created {new Date(l.created_at).toLocaleString()}</span>
                        <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{l.hazard_class}</span>
                      </div>
                    </CardContent>
                  </Card>
                  ));
                })()}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
